import React, { useEffect, useMemo, useRef } from 'react';
import { IonPage, IonActionSheet, useIonViewDidEnter } from '@ionic/react';
import { useShareWizardContext } from '@/contexts/ShareWizardContext';
import { ContactLogic } from '@/modules/contact';
import AddContactModal from '@/components/AddContactModal';
import { Destinations, ShareNavigationLogic } from './navigation_logic';

/**
 * Recover from a cancelled enter transition that leaves the share flow's page
 * invisible (a blank screen). Returning here via goBack() from a sibling root
 * route — the profile Attribution detour's "Back to sharing" — remounts this
 * page, and @ionic/react-router's StackManager then resolves the outlet's
 * leaving view by the PREVIOUS route's pathname (`/topics/:id/share`). Because
 * the flow's Route is non-exact, that pathname matches this same fresh
 * view, so the router sees entering === leaving with route data that doesn't
 * equal the current destination path and cancels the transition as if a parent
 * outlet were re-rendering (StackManager.handlePageTransition). Nothing ever
 * removes `ion-page-invisible` from the page and its ionViewDidEnter never
 * fires — but the inner destination outlet still transitions normally. A
 * destination page's ionViewDidEnter therefore marks the moment this page must
 * be visible: listen for it in the capture phase (lifecycle events don't
 * bubble), and if the page is still marked invisible or hidden then, unhide it
 * and call `onRecovered` in place of the enter transition that never ran.
 */
export const useRecoverCancelledEnterTransition = (
  pageRef: React.RefObject<HTMLElement | null>,
  onRecovered: () => void
) => {
  useEffect(() => {
    const page = pageRef.current;
    if (!page) return;
    const unwedge = () => {
      if (
        page.classList.contains('ion-page-invisible') ||
        page.classList.contains('ion-page-hidden')
      ) {
        page.classList.remove('ion-page-invisible', 'ion-page-hidden');
        page.removeAttribute('aria-hidden');
        onRecovered();
      }
    };
    page.addEventListener('ionViewDidEnter', unwedge, true);
    return () => page.removeEventListener('ionViewDidEnter', unwedge, true);
  }, [pageRef, onRecovered]);
};

/**
 * The page a sharing session lives on, whichever surface renders it.
 *
 * Everything here belongs to the session rather than to any one destination:
 * the page itself and its two transition rescues, the guard that refuses a
 * sending destination with nothing to send, the contact modals the recipient
 * surfaces raise, and the two warnings shown on the way out. The surface
 * supplies only its own `IonRouterOutlet` as `children`, so the wizard's eight
 * steps and the garden flow's three modals sit on one shell.
 */
const ShareFlowShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    addContactInitialName,
    showAddContactModal,
    handleCloseAddContactModal,
    handleSaveAddedContact,
    nav,
    isLoadingLegState,
    sendQueue,
    confirmedContactIds,
    handleInterruptSending,
    showInterruptWarning,
    setShowInterruptWarning,
    showCloseWarning,
    setShowCloseWarning,
    totalConfirmedCount,
    notifyViewSettled,
  } = useShareWizardContext();

  // Tell the provider the flow page's enter transition has finished, gating
  // the deferred pre-send recipient restore (#479). The restore dispatches into
  // ContactStateContext above the outlet; doing that while the page is still
  // transitioning in (the set-names detour return) re-renders the outlet and
  // wedges the page blank, so the provider waits for this signal. (Only the
  // animated warm-remount case needs it: a cold load has no transition, and the
  // provider applies the restore as soon as the address book loads, independent
  // of this signal — the outer page's ionViewDidEnter does not fire on a cold
  // load in this nested outlet anyway.)
  useIonViewDidEnter(() => {
    notifyViewSettled();
  });

  // The Attribution-detour return can leave this page wedged invisible (see
  // useRecoverCancelledEnterTransition); recover by unhiding it and reporting
  // the view settled when a destination page enters while it is still hidden.
  const pageRef = useRef<HTMLElement | null>(null);
  useRecoverCancelledEnterTransition(pageRef, notifyViewSettled);

  const isSendingDestination = ShareNavigationLogic.isSending(nav.at);

  // Sending destinations only: snap back to recipients if the flow is entered with
  // nothing queued or confirmed (e.g. a reload landed on a share/confirm step with no
  // in-progress send), so the user restarts from a valid state.
  //
  // We deliberately do NOT snap the earlier destinations back to recipients
  // when the recipient list is empty. That list is in-memory
  // only, so it's empty after any remount — including the round trip out to the
  // Attribution step to set profile attribution info and back. Snapping there fired a navigation
  // while the freshly-restored share page was still transitioning in,
  // wedging it at `ion-page-invisible` (a blank page). Those destinations already
  // disable Next while recipients is empty, so leaving the user there
  // (able to step Back to re-pick) is safe. Preserving the recipient selection
  // across the Attribution-step detour is tracked separately.
  useEffect(() => {
    if (isLoadingLegState) {
      return;
    }
    if (
      isSendingDestination &&
      totalConfirmedCount === 0 &&
      sendQueue.length === 0
    ) {
      nav.goTo(Destinations.recipients, 'none');
    }
  }, [
    isSendingDestination,
    isLoadingLegState,
    sendQueue.length,
    totalConfirmedCount,
    nav,
  ]);

  const addContactTemplate = useMemo(
    () => ContactLogic.createPartialFromHint(addContactInitialName),
    [addContactInitialName]
  );

  const interruptWarningMessage =
    confirmedContactIds.size === 0
      ? 'Are you sure you want to interrupt sending? Some messages were not marked as sent, which could lead to recipients getting duplicates later.'
      : "Are you sure you want to interrupt sending? Some recipients haven't been messaged yet.";

  const closeWarningMessage =
    confirmedContactIds.size === 0
      ? 'Close sharing? Some messages were not marked as sent, which could lead to recipients getting duplicates later.'
      : "Close sharing? Some recipients haven't been messaged yet.";

  return (
    <IonPage className="share-wizard" ref={pageRef}>
      {children}

      <AddContactModal
        isOpen={showAddContactModal}
        onClose={handleCloseAddContactModal}
        contact={addContactTemplate}
        onSave={handleSaveAddedContact}
      />

      <IonActionSheet
        isOpen={showInterruptWarning}
        onDidDismiss={() => setShowInterruptWarning(false)}
        header={interruptWarningMessage}
        buttons={[
          {
            text: 'Interrupt anyway',
            role: 'destructive',
            handler: () => {
              setShowInterruptWarning(false);
              handleInterruptSending();
            },
          },
          { text: 'Keep messaging', role: 'cancel' },
        ]}
      />

      <IonActionSheet
        isOpen={showCloseWarning}
        onDidDismiss={() => setShowCloseWarning(false)}
        header={closeWarningMessage}
        buttons={[
          {
            text: 'Close',
            role: 'destructive',
            handler: () => {
              setShowCloseWarning(false);
              nav.exitAndDiscard();
            },
          },
          { text: 'Keep messaging', role: 'cancel' },
        ]}
      />
    </IonPage>
  );
};

export default ShareFlowShell;
